/**
 * Operator Cockpit — calm "command center" surface for venue / academy /
 * organizer hosts (Phase 178.6).
 *
 * DESIGN DOCTRINE
 * ───────────────
 * Answers exactly one question for the operator:
 *
 *     "What needs my attention right now?"
 *
 * NOT "what data exists?"
 *
 * That means:
 *   • 6 cards.  No more, no less.  Predictable cognitive load.
 *   • Each card is one numeric / status signal — no charts, no tables.
 *   • Calm typography (no gradient text, no oversized numerals).
 *   • Disabled cards for un-shipped backend data render as muted
 *     placeholders — same visual hierarchy, just less colour.  This is the
 *     same intent-color discipline used in `<BulletproofContextMenu>`.
 *   • Intent variants on each card tint only the eyebrow + value glyph —
 *     never the whole card.  Avoids analytics-dashboard chaos.
 *
 * USAGE
 * ─────
 *   <OperatorCockpit kind="venue"     entityId={venueId} />
 *   <OperatorCockpit kind="academy"   entityId={academyId} />
 *   <OperatorCockpit kind="organizer" entityId={organizerId} />
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import {
  Calendar, Activity, IndianRupee, Flame, AlertTriangle, MapPin,
  Users, GraduationCap, CheckCircle2, BarChart3, Trophy,
  Award, Megaphone, Gavel,
} from 'lucide-react';

const INTENT = {
  neutral: { dot: 'hsl(var(--ax-text-muted))', eyebrow: 'hsl(var(--ax-text-faint))', tint: 'transparent' },
  success: { dot: '#10b981', eyebrow: '#065f46', tint: 'rgba(16,185,129,0.06)' },
  warning: { dot: '#f59e0b', eyebrow: '#92400e', tint: 'rgba(245,158,11,0.06)' },
  danger:  { dot: 'hsl(0 70% 55%)', eyebrow: 'hsl(0 70% 45%)', tint: 'rgba(244,63,94,0.05)' },
};

function CockpitCard({ icon: Icon, eyebrow, value, hint, variant = 'neutral', disabled = false, testid }) {
  const tone = INTENT[variant] || INTENT.neutral;
  return (
    <div
      className="rounded-xl p-4 border transition-colors"
      style={{
        borderColor: 'var(--ax-border-quiet)',
        background: disabled ? 'var(--ax-card-bg-soft)' : (variant === 'neutral' ? 'var(--ax-card-bg-soft)' : tone.tint),
        opacity: disabled ? 0.55 : 1,
      }}
      data-testid={testid}
      aria-disabled={disabled || undefined}
    >
      <div className="flex items-center justify-between mb-2">
        <div
          className="text-[10px] uppercase tracking-[0.25em] font-bold"
          style={{ color: disabled ? 'hsl(var(--ax-text-faint))' : tone.eyebrow }}
        >
          {eyebrow}
        </div>
        {Icon ? (
          <Icon
            className="h-3.5 w-3.5"
            style={{ color: disabled ? 'hsl(var(--ax-text-faint))' : tone.dot }}
          />
        ) : null}
      </div>
      <div
        className="text-2xl font-light tracking-tight"
        style={{
          color: 'hsl(var(--ax-text))',
          fontFamily: 'Georgia, "Times New Roman", serif',
        }}
      >
        {disabled ? '—' : value}
      </div>
      <div className="text-[11px] mt-1" style={{ color: 'hsl(var(--ax-text-muted))' }}>
        {disabled ? 'Coming soon' : hint}
      </div>
    </div>
  );
}

// ── Per-kind card builders ──────────────────────────────────────────

function buildVenueCards(d, pulse) {
  const today = d?.today || {};
  // Phase 178.6.G Part 2 — recent no-shows + city demand pulse
  const ns = pulse?.recent_no_shows || null;
  const noShowEmpty = !ns || (ns.count_30d ?? 0) === 0;
  const cdp = pulse?.city_demand_pulse || null;
  const topCity = cdp?.top_cities?.[0];
  const cityEmpty = !cdp || (cdp.total_bookings ?? 0) === 0;
  return [
    { icon: Calendar, eyebrow: "Today's bookings",
      value: today.confirmed_slots ?? 0,
      hint: `${today.total_slots ?? 0} slots scheduled`,
      variant: 'success', testid: 'cockpit-venue-today-bookings' },
    { icon: Activity, eyebrow: 'Occupancy',
      value: `${today.occupancy_pct ?? 0}%`,
      hint: 'Confirmed vs scheduled',
      variant: 'neutral', testid: 'cockpit-venue-occupancy' },
    { icon: IndianRupee, eyebrow: 'Revenue today',
      value: `₹${(today.revenue ?? 0).toLocaleString('en-IN')}`,
      hint: 'Confirmed + completed',
      variant: 'success', testid: 'cockpit-venue-revenue' },
    { icon: Flame, eyebrow: 'Upcoming peak slots',
      value: d?.next_slot ? '1 next' : '—',
      hint: d?.next_slot
        ? new Date(d.next_slot.start_at).toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' })
        : 'No confirmed slots in pipeline',
      variant: 'warning',
      disabled: !d?.next_slot,
      testid: 'cockpit-venue-peak' },
    { icon: AlertTriangle, eyebrow: 'Recent no-shows',
      value: noShowEmpty ? '—' : ns.count_7d,
      hint: noShowEmpty
        ? 'No no-shows in 30d'
        : `${ns.count_7d} last 7d · ${ns.count_30d} last 30d`,
      variant: 'danger',
      disabled: noShowEmpty,
      testid: 'cockpit-venue-no-shows' },
    { icon: MapPin, eyebrow: 'City demand pulse',
      value: cityEmpty ? '—' : (topCity?.city || 'Unknown'),
      hint: cityEmpty
        ? 'No demand yet'
        : `${topCity?.count || 0} bookings · top of ${cdp.top_cities.length}`,
      variant: 'neutral',
      disabled: cityEmpty,
      testid: 'cockpit-venue-city-pulse' },
  ];
}

function buildAcademyCards(d, pulse) {
  // d is the flat overview response from /arenax/host/academy/{id}/overview
  // pulse is the cockpit-pulse extension (Phase 178.6.G Part 2 + 178.6.I)
  const activeStudents = d?.active_students ?? 0;
  const coaches = d?.coach_count ?? 0;
  const att = pulse?.attendance_pulse || null;
  const attEmpty = !att || (att.total ?? 0) === 0;
  const fx = pulse?.upcoming_fixtures || null;
  const fxEmpty = !fx || (fx.count ?? 0) === 0;
  // Phase 178.6.I — academy ranking
  const rk = pulse?.academy_ranking || null;
  const rkEmpty = !rk || rk.rank == null;
  const rankLabel = rk && rk.total > 1 ? `#${rk.rank} of ${rk.total}` : (rk?.rank ? `#${rk.rank}` : '—');
  return [
    { icon: Users, eyebrow: 'Active students',
      value: activeStudents,
      hint: `${coaches} coaches`,
      variant: 'success', testid: 'cockpit-academy-students' },
    { icon: GraduationCap, eyebrow: 'Coach load',
      value: coaches,
      hint: coaches ? `${Math.round(activeStudents / Math.max(coaches, 1))} students / coach` : 'No coaches yet',
      variant: 'neutral', testid: 'cockpit-academy-coach-load' },
    { icon: Calendar, eyebrow: 'Upcoming sessions',
      value: fxEmpty ? '—' : fx.count,
      hint: fxEmpty ? 'No sessions in pipeline' : (fx.next_at ? `Next ${fx.next_at}` : 'Scheduled'),
      variant: 'neutral',
      disabled: fxEmpty,
      testid: 'cockpit-academy-upcoming' },
    { icon: CheckCircle2, eyebrow: 'Attendance pulse',
      value: attEmpty ? '—' : `${att.rate_pct ?? 0}%`,
      hint: attEmpty
        ? 'No attendance marked in 7d'
        : `${att.present}/${att.total} present · ${att.sessions} sessions`,
      variant: 'success',
      disabled: attEmpty,
      testid: 'cockpit-academy-attendance' },
    { icon: Trophy, eyebrow: 'Upcoming fixtures',
      value: fxEmpty ? '—' : fx.count,
      hint: fxEmpty
        ? 'No fixtures scheduled'
        : (fx.next_title ? `Next: ${fx.next_title}` : (fx.next_at || 'Scheduled')),
      variant: 'warning',
      disabled: fxEmpty,
      testid: 'cockpit-academy-fixtures' },
    { icon: Award, eyebrow: 'Academy ranking',
      value: rkEmpty ? '—' : rankLabel,
      hint: rkEmpty
        ? 'No peers in your city yet'
        : `${rk.percentile != null ? rk.percentile + '%ile' : '—'} in ${rk.city || 'your city'}`,
      variant: 'neutral',
      disabled: rkEmpty,
      testid: 'cockpit-academy-ranking' },
  ];
}

function buildOrganizerCards(d, pulse) {
  // d is the flat overview response from /arenax/host/organizer/{id}/overview
  // pulse is the cockpit-pulse extension (Phase 178.6.G Part 2 + 178.6.I)
  const activeLeagues = d?.active_leagues ?? 0;
  const upcomingSeasons = d?.upcoming_seasons ?? 0;
  const registeredSquads = d?.registered_squads ?? 0;
  const bp = pulse?.bracket_progress || null;
  const bpEmpty = !bp || (bp.total_matches ?? 0) === 0;
  const fx = pulse?.upcoming_fixtures || null;
  const fxEmpty = !fx || (fx.count ?? 0) === 0;
  // Phase 178.6.I — audience growth + pending disputes
  const ag = pulse?.audience_growth || null;
  const agEmpty = !ag || (ag.signups_30d ?? 0) === 0;
  const agTrend = ag?.trend || 'flat';
  const pd = pulse?.pending_disputes || null;
  const pdEmpty = !pd || (pd.total ?? 0) === 0;
  return [
    { icon: Trophy, eyebrow: 'Active leagues',
      value: activeLeagues,
      hint: upcomingSeasons ? `${upcomingSeasons} upcoming seasons` : 'No upcoming seasons',
      variant: 'success', testid: 'cockpit-organizer-tournaments' },
    { icon: Users, eyebrow: 'Registered squads',
      value: registeredSquads,
      hint: 'Across all leagues',
      variant: 'neutral', testid: 'cockpit-organizer-registrations' },
    { icon: BarChart3, eyebrow: 'Bracket progress',
      value: bpEmpty ? '—' : `${bp.percent_complete ?? 0}%`,
      hint: bpEmpty
        ? 'No matches scheduled'
        : `${bp.completed_matches}/${bp.total_matches} matches · ${bp.leagues_in_progress} live`,
      variant: 'success',
      disabled: bpEmpty,
      testid: 'cockpit-organizer-bracket' },
    { icon: Calendar, eyebrow: 'Upcoming fixtures',
      value: fxEmpty ? '—' : fx.count,
      hint: fxEmpty
        ? 'No fixtures in pipeline'
        : (fx.next_title ? `Next: ${fx.next_title}` : (fx.next_at || 'Scheduled')),
      variant: 'neutral',
      disabled: fxEmpty,
      testid: 'cockpit-organizer-fixtures' },
    { icon: Megaphone, eyebrow: 'Audience growth',
      value: agEmpty ? '—' : (ag.growth_pct != null
        ? `${ag.growth_pct >= 0 ? '+' : ''}${ag.growth_pct}%`
        : `${ag.signups_7d}`),
      hint: agEmpty
        ? 'No signups in 30d'
        : `${ag.signups_7d} signups · ${agTrend === 'up' ? 'accelerating' : agTrend === 'down' ? 'cooling' : 'steady'}`,
      variant: agTrend === 'up' ? 'success' : (agTrend === 'down' ? 'danger' : 'warning'),
      disabled: agEmpty,
      testid: 'cockpit-organizer-audience' },
    { icon: Gavel, eyebrow: 'Pending disputes',
      value: pdEmpty ? '—' : pd.total,
      hint: pdEmpty
        ? 'All clear'
        : `${pd.refund_queue} queued refunds · ${pd.disputed_bookings} disputed`,
      variant: 'danger',
      disabled: pdEmpty,
      testid: 'cockpit-organizer-disputes' },
  ];
}

const BUILDERS = {
  venue: buildVenueCards,
  academy: buildAcademyCards,
  organizer: buildOrganizerCards,
};

const OVERVIEW_PATH = {
  venue: 'venue',
  academy: 'academy',
  organizer: 'organizer',
};

export default function OperatorCockpit({ kind, entityId, overview: overviewProp = null }) {
  // If the parent has already fetched the overview (which the manage pages
  // do), we re-use it.  Otherwise we fetch lazily.  Both paths converge.
  const [data, setData] = useState(overviewProp);
  const [pulse, setPulse] = useState(null);   // Phase 178.6.G Part 2 — cockpit-pulse
  const [loading, setLoading] = useState(!overviewProp);

  useEffect(() => {
    if (overviewProp) {
      setData(overviewProp);
    }
    if (!entityId || !kind || !OVERVIEW_PATH[kind]) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const calls = [
      api.get(`/arenax/host/${OVERVIEW_PATH[kind]}/${entityId}/cockpit-pulse`)
        .then((r) => { if (!cancelled) setPulse(r.data); })
        .catch(() => { /* non-fatal — disabled tiles stay disabled */ }),
    ];
    if (!overviewProp) {
      calls.push(
        api.get(`/arenax/host/${OVERVIEW_PATH[kind]}/${entityId}/overview`)
          .then((r) => { if (!cancelled) setData(r.data); })
          .catch(() => { /* non-fatal — cards render as placeholders */ })
      );
    }
    Promise.all(calls).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [kind, entityId, overviewProp]);

  const builder = BUILDERS[kind];
  if (!builder) return null;
  const cards = builder(data, pulse);

  return (
    <section
      className="space-y-3"
      data-testid={`operator-cockpit-${kind}`}
      aria-label={`${kind} operator cockpit`}
    >
      <div className="flex items-baseline justify-between">
        <h2
          className="text-[11px] uppercase tracking-[0.30em] font-bold"
          style={{ color: 'hsl(var(--ax-text-faint))' }}
          data-testid={`cockpit-${kind}-heading`}
        >
          Today
        </h2>
        <span className="text-[10px]" style={{ color: 'hsl(var(--ax-text-faint))' }}>
          {loading ? 'syncing…' : 'live'}
        </span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((c, i) => (
          <CockpitCard key={c.testid || i} {...c} />
        ))}
      </div>
    </section>
  );
}
